/**
 * Route Definitions
 * Centralized route configuration for the application
 */
import { ROLES } from './constants';

export const ROUTES = {
  // ─── Public Routes ───
  LOGIN: '/login',
  PUBLIC_RESULTS: '/public/results',
  VERIFY_RESULT: '/verify/result/:resultId',
  VERIFY_STUDENT: '/verify/student/:studentId',
  MAINTENANCE: '/maintenance',

  // ─── Protected Routes ───
  DASHBOARD: '/dashboard',
  ACADEMIC_YEAR: '/academic-year',
  SETUP: '/setup',

  // ─── Admin Routes ───
  SUBJECTS: '/subjects',
  CLASS_SUBJECTS: '/class-subjects',
  TEACHERS: '/teachers',
  TEACHER_ASSIGNMENTS: '/teacher-assignments',
  STUDENTS: '/students',
  EXAMS: '/exams',
  EXAM_DETAIL: '/exams/:id',
  EXAM_ENROLLMENTS: '/exams/:id/enrollments',
  ENROLLMENTS: '/enrollments',
  MARK_MANAGEMENT: '/marks',
  UPDATE_REQUESTS: '/update-requests',
  RESULTS: '/results',
  RESULT_SEARCH: '/result-search',
  RESULT_DETAIL: '/results/:examId',
  REPORTS: '/reports',
  TABULATION_SHEET: '/reports/tabulation',
  CLASS_MARKSHEETS: '/reports/marksheets',
  EXAM_ADMIT: '/reports/exam-admit',
  GRADING_POLICY: '/grading-policy',
  AUDIT_LOGS: '/audit-logs',
  SETTINGS: '/settings',

  // ─── Notification Center ───
  NOTICE_BOARD: '/notifications/notice-board',
  NOTIFICATIONS: '/notifications',

  // ─── Teacher Routes ───
  TEACHER_DASHBOARD: '/teacher/dashboard',
  TEACHER_MARKS: '/teacher/marks/:assignmentId',
  MARK_ENTRY: '/mark-entry',
};

/**
 * Navigation items for sidebar
 * Filtered by user role at render time
 */
export const NAV_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: ROUTES.DASHBOARD,
    icon: 'LayoutDashboard',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TEACHER],
  },
  {
    id: 'public-result',
    label: 'Public Result',
    path: ROUTES.PUBLIC_RESULTS,
    icon: 'Search',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.TEACHER],
  },
  {
    id: 'academic-year',
    label: 'Academic Year',
    path: ROUTES.ACADEMIC_YEAR,
    icon: 'Calendar',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
  },
  {
    id: 'exam-management',
    label: 'Exam Management',
    icon: 'ClipboardList',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'exams', label: 'Exam Sessions', path: ROUTES.EXAMS, icon: 'Calendar' },
      { id: 'enrollments', label: 'Enrollment & Eligibility', path: ROUTES.ENROLLMENTS, icon: 'UserCheck' },
    ],
  },
  {
    id: 'academics',
    label: 'Academics',
    icon: 'GraduationCap',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'subjects', label: 'Subjects', path: ROUTES.SUBJECTS, icon: 'BookOpen' },
      { id: 'class-subjects', label: 'Class Subjects', path: ROUTES.CLASS_SUBJECTS, icon: 'Layers' },
      { id: 'grading-policy', label: 'Grading Policy', path: ROUTES.GRADING_POLICY, icon: 'Award' },
    ],
  },
  {
    id: 'people',
    label: 'People',
    icon: 'Users',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'teachers', label: 'Teachers', path: ROUTES.TEACHERS, icon: 'UserCog' },
      { id: 'teacher-assignments', label: 'Assignments', path: ROUTES.TEACHER_ASSIGNMENTS, icon: 'Link' },
    ],
  },
  {
    id: 'marks',
    label: 'Marks',
    icon: 'PenTool',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'mark-management', label: 'Mark Management', path: ROUTES.MARK_MANAGEMENT, icon: 'FileEdit' },
      { id: 'update-requests', label: 'Update Requests', path: ROUTES.UPDATE_REQUESTS, icon: 'MessageSquare' },
    ],
  },
  {
    id: 'mark-entry',
    label: 'Mark Entry',
    path: ROUTES.MARK_ENTRY,
    icon: 'PenTool',
    roles: [ROLES.TEACHER],
  },
  {
    id: 'results',
    label: 'Results',
    icon: 'Trophy',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'result-generator', label: 'Result Generator', path: ROUTES.RESULTS, icon: 'FileEdit' },
      { id: 'result-search', label: 'Result Search', path: ROUTES.RESULT_SEARCH, icon: 'Search' },
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: 'BarChart3',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'reports-dashboard', label: 'Dashboard', path: ROUTES.REPORTS, icon: 'BarChart3' },
      { id: 'tabulation-sheet', label: 'Tabulation Sheet', path: ROUTES.TABULATION_SHEET, icon: 'FileText' },
      { id: 'class-marksheets', label: 'Class Marksheets', path: ROUTES.CLASS_MARKSHEETS, icon: 'ClipboardList' },
      { id: 'exam-admit', label: 'Exam Admit', path: ROUTES.EXAM_ADMIT, icon: 'UserCheck' },
    ],
  },
  {
    id: 'notification-center',
    label: 'Notification Center',
    icon: 'Bell',
    roles: [ROLES.SUPER_ADMIN, ROLES.ADMIN],
    children: [
      { id: 'notice-board', label: 'Notice Board', path: ROUTES.NOTICE_BOARD, icon: 'FileEdit' },
      { id: 'notifications', label: 'Notifications', path: ROUTES.NOTIFICATIONS, icon: 'Bell' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: 'Settings',
    roles: [ROLES.SUPER_ADMIN],
    children: [
      { id: 'setup', label: 'Setup Wizard', path: ROUTES.SETUP, icon: 'Wand2' },
      { id: 'settings', label: 'Settings', path: ROUTES.SETTINGS, icon: 'Settings' },
      { id: 'audit-logs', label: 'Audit Logs', path: ROUTES.AUDIT_LOGS, icon: 'FileText' },
    ],
  },
];
